import React, { useState, useEffect } from 'react';
import { twoFactorAPI } from '../services/api';
import { toast } from 'react-hot-toast';
import { Shield, Key, X, Check } from 'lucide-react';

const TwoFactorSettings: React.FC = () => {
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const response = await twoFactorAPI.getStatus();
      setIs2FAEnabled(response.data.enabled);
    } catch (error) {
      console.error('Failed to load 2FA status:', error);
    }
  };

  const handleSetup = async () => {
    setLoading(true);
    try {
      const response = await twoFactorAPI.setup();
      setQrCodeUrl(response.data.qr_code_url);
      setSecret(response.data.secret);
      setShowSetupModal(true);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to setup 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    try {
      await twoFactorAPI.verify(verificationCode);
      toast.success('Two-factor authentication enabled successfully!');
      setIs2FAEnabled(true);
      setShowSetupModal(false);
      setVerificationCode('');
      setQrCodeUrl('');
      setSecret('');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!disablePassword) {
      toast.error('Please enter your password');
      return;
    }

    setLoading(true);
    try {
      await twoFactorAPI.disable(disablePassword);
      toast.success('Two-factor authentication disabled');
      setIs2FAEnabled(false);
      setShowDisableModal(false);
      setDisablePassword('');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* 2FA Status Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className={`p-3 rounded-lg ${is2FAEnabled ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
              <Shield className={`h-6 w-6 ${is2FAEnabled ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}`} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                Two-Factor Authentication
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Add an extra layer of security to your account using an authenticator app.
              </p>
              <div className="flex items-center space-x-2">
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                  is2FAEnabled 
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {is2FAEnabled ? (
                    <div className="flex items-center space-x-1">
                      <Check className="h-3 w-3" />
                      <span>Enabled</span>
                    </div>
                  ) : (
                    'Disabled'
                  )}
                </div>
              </div>
            </div>
          </div>
          <div>
            {is2FAEnabled ? (
              <button
                onClick={() => setShowDisableModal(true)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Disable 2FA
              </button>
            ) : (
              <button
                onClick={handleSetup}
                disabled={loading}
                className="px-4 py-2 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                {loading ? 'Setting up...' : 'Enable 2FA'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Setup Modal */}
      {showSetupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Set Up Two-Factor Authentication
              </h3>
              <button
                onClick={() => setShowSetupModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Step 1 */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Step 1: Scan QR Code
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Open your authenticator app (Google Authenticator, Authy, etc.) and scan this QR code:
                </p>
                <div className="flex justify-center bg-white p-4 rounded-lg">
                  <img src={qrCodeUrl} alt="2FA QR Code" className="w-48 h-48" />
                </div>
              </div>

              {/* Step 2 */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Step 2: Save Your Secret Key
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  If you can't scan the QR code, enter this secret key manually:
                </p>
                <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg font-mono text-sm text-center break-all">
                  {secret}
                </div>
              </div>

              {/* Step 3 */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Step 3: Enter Verification Code
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  Enter the 6-digit code from your authenticator app:
                </p>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-center font-mono text-xl tracking-widest focus:ring-2 focus:ring-offset-0 focus:outline-none"
                  style={{ '--tw-ring-color': 'var(--color-primary)' } as any}
                  maxLength={6}
                  autoFocus
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowSetupModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleVerify}
                  disabled={loading || verificationCode.length !== 6}
                  className="flex-1 px-4 py-2 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                  style={{ backgroundColor: 'var(--color-primary)' }}
                >
                  {loading ? 'Verifying...' : 'Verify & Enable'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disable Modal */}
      {showDisableModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Disable Two-Factor Authentication
              </h3>
              <button
                onClick={() => setShowDisableModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-sm text-red-800 dark:text-red-200">
                  <strong>Warning:</strong> Disabling 2FA will make your account less secure. You'll only need your password to log in.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Enter your password to confirm
                </label>
                <input
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  placeholder="Your password"
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-offset-0 focus:outline-none"
                  style={{ '--tw-ring-color': 'var(--color-primary)' } as any}
                  autoFocus
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDisableModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisable}
                  disabled={loading || !disablePassword}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {loading ? 'Disabling...' : 'Disable 2FA'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwoFactorSettings;











